import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { TabPanel, TabView } from "primereact/tabview";
import { Tooltip } from "primereact/tooltip";
import React, { useMemo } from "react";
import styled from "styled-components";

import { parseDate } from "../../helpers/date";
import { IClaim, ILineItem, ILineItemAdjustment } from "../../types/claim";
import { PrecalculatedAllowablesContent } from "../PrecalculatedAllowablesModal/PrecalculatedAllowablesContent";
import { AIInsightsTabContent } from "./AIInsightsTabContent";
import { EstimateComparisonTab } from "./EstimateComparisonTab";

// Adjustment code descriptions based on CARC (Claim Adjustment Reason Codes)
const ADJUSTMENT_CODE_DESCRIPTIONS: Record<string, string> = {
  "1": "Deductible",
  "2": "Coinsurance",
  "3": "Copayment",
  "23": "Payment adjusted due to impact of prior payer(s) adjudication",
  "45": "Charge exceeds fee schedule/maximum allowable",
  "94": "Processed in excess of charges",
  "96": "Non-covered charges (patient liable)",
  "97": "Payment included in another service",
  "109": "Claim not covered by this payer, submit to another",
  "204": "Service not covered (patient responsible)",
  "253": "Sequestration reduction",
  "273": "Coverage/program guidelines were exceeded",
};

interface IClaimDetailsModalProps {
  visible: boolean;
  onHide: () => void;
  claim: IClaim;
  accountId: string;
}

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem 2rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DetailRow = styled.div`
  display: flex;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e9ecef;

  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.div`
  font-weight: 600;
  min-width: 150px;
  margin-right: 0.75rem;
  color: #495057;
`;

const DetailValue = styled.div`
  color: #212529;
  flex: 1;
`;

const PlaceholderText = styled.div`
  padding: 2rem;
  text-align: center;
  color: #6c757d;
  font-size: 14px;
`;

const ScrollableTabContent = styled.div`
  height: 60vh;
  max-height: 650px;
  overflow-y: auto;
  padding: 0;
`;

const InfoIcon = styled.i`
  font-size: 0.75rem;
  color: #6c757d;
  margin-left: 4px;
  cursor: help;
  opacity: 0.7;

  &:hover {
    opacity: 1;
    color: #495057;
  }
`;

/**
 * Modal component to display comprehensive claim details
 * with three tabs: Claim Details, Allowable Calculations, and AI
 */
export function ClaimDetailsModal({
  visible,
  onHide,
  claim,
  accountId,
}: IClaimDetailsModalProps) {
  // Currency formatter
  const formatCurrency = (cents: number | null): string => {
    if (cents === null || cents === undefined) return "-";
    const dollars = cents / 100;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(dollars);
  };

  // Format date helper
  const formatDate = (date: Date | null): string => {
    return date ? parseDate(date) : "-";
  };

  // Line items data
  const lineItems: ILineItem[] = useMemo(() => {
    return claim.lineItems || [];
  }, [claim.lineItems]);

  // Line item templates
  const currencyBodyTemplate = (rowData: ILineItem, field: keyof ILineItem) => {
    const value = rowData[field];
    if (typeof value === "number") {
      return formatCurrency(Math.round(value * 100)); // Convert dollars to cents
    }
    return "-";
  };

  // Patient responsibility (sum of all PR adjustments)
  const patientResponsibilityTemplate = (rowData: ILineItem) => {
    // Use the new patientResponsibility field if available
    if (
      rowData.patientResponsibility !== undefined &&
      rowData.patientResponsibility !== null
    ) {
      return rowData.patientResponsibility > 0
        ? `$${rowData.patientResponsibility.toFixed(2)}`
        : "-";
    }

    // Fallback: calculate from adjustments if the field doesn't exist yet
    if (!rowData.adjustments || rowData.adjustments.length === 0) {
      return "-";
    }
    const prTotal = rowData.adjustments
      .filter((adj) => adj.group === "PR")
      .reduce((sum, adj) => sum + adj.amount, 0);
    return prTotal > 0 ? `$${prTotal.toFixed(2)}` : "-";
  };

  // Get only PR adjustments from a line item
  const getPrAdjustments = (lineItem: ILineItem): ILineItemAdjustment[] => {
    return lineItem.adjustments?.filter((adj) => adj.group === "PR") || [];
  };

  // Calculate max number of PR adjustments across all line items
  const maxPrAdjustments = useMemo(() => {
    if (!lineItems || lineItems.length === 0) return 0;
    return Math.max(
      ...lineItems.map((item) => getPrAdjustments(item).length),
      0
    );
  }, [lineItems]);

  // PR adjustment code with tooltip
  const prAdjustmentCodeTemplate = (rowData: ILineItem, prIndex: number) => {
    const prAdjs = getPrAdjustments(rowData);
    const prAdj = prAdjs[prIndex];
    if (!prAdj) return "-";

    const { code } = prAdj;
    const description = ADJUSTMENT_CODE_DESCRIPTIONS[code];

    if (description) {
      return (
        <span>
          {code}
          <InfoIcon
            className="adj-code-tooltip pi pi-info-circle"
            data-pr-tooltip={description}
          />
        </span>
      );
    }
    return code;
  };

  // PR adjustment amount template
  const prAdjustmentAmountTemplate = (rowData: ILineItem, prIndex: number) => {
    const prAdjs = getPrAdjustments(rowData);
    if (!prAdjs[prIndex]) return "-";
    return `$${prAdjs[prIndex].amount.toFixed(2)}`;
  };

  // Generate dynamic PR adjustment columns
  const renderPrAdjustmentColumns = () => {
    const columns: React.ReactNode[] = [];

    for (let i = 0; i < maxPrAdjustments; i += 1) {
      columns.push(
        <Column
          key={`pr-adj-${i}-code`}
          header={`PR ${i + 1} Code`}
          body={(rowData: ILineItem) => prAdjustmentCodeTemplate(rowData, i)}
          style={{ minWidth: "6rem" }}
        />,
        <Column
          key={`pr-adj-${i}-amount`}
          header={`PR ${i + 1} Amt`}
          body={(rowData: ILineItem) => prAdjustmentAmountTemplate(rowData, i)}
          style={{ minWidth: "6rem" }}
        />
      );
    }

    return columns;
  };

  return (
    <Dialog
      header="Claim Details"
      visible={visible}
      onHide={onHide}
      style={{ width: "95vw", maxWidth: "1800px" }}
      modal
      dismissableMask
    >
      <TabView>
        {/* Tab 1: Claim Details */}
        <TabPanel header="Claim Details">
          <ScrollableTabContent>
            <div style={{ padding: "1rem" }}>
              <Tooltip
                target=".adj-code-tooltip"
                position="top"
                showDelay={0}
                style={{ zIndex: 10000 }}
              />
              <DetailGrid>
                {/* Left Column - Patient Information */}
                <div>
                  <h3
                    style={{
                      marginTop: 0,
                      marginBottom: "1rem",
                      fontSize: "16px",
                      fontWeight: 600,
                    }}
                  >
                    Patient Information
                  </h3>
                  <DetailRow>
                    <DetailLabel>Patient Name:</DetailLabel>
                    <DetailValue>{claim.patientName}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Patient DOB:</DetailLabel>
                    <DetailValue>{formatDate(claim.patientDOB)}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Account #:</DetailLabel>
                    <DetailValue>
                      {claim.patientAccountNumber || "-"}
                    </DetailValue>
                  </DetailRow>

                  <h3
                    style={{
                      marginTop: "1.5rem",
                      marginBottom: "1rem",
                      fontSize: "16px",
                      fontWeight: 600,
                    }}
                  >
                    Claim Information
                  </h3>
                  <DetailRow>
                    <DetailLabel>Claim #:</DetailLabel>
                    <DetailValue>{claim.claimNumber || "-"}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Service Date:</DetailLabel>
                    <DetailValue>{formatDate(claim.serviceDate)}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Payer Name:</DetailLabel>
                    <DetailValue>{claim.claimPayerName}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Group #:</DetailLabel>
                    <DetailValue>
                      {claim.claimPayerGroupNumber || "-"}
                    </DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Network Status:</DetailLabel>
                    <DetailValue>{claim.claimNetworkStatus || "-"}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Matched Payer:</DetailLabel>
                    <DetailValue>{claim.clearPayerName || "-"}</DetailValue>
                  </DetailRow>
                </div>

                {/* Right Column - Financial Summary */}
                <div>
                  <h3
                    style={{
                      marginTop: 0,
                      marginBottom: "1rem",
                      fontSize: "16px",
                      fontWeight: 600,
                    }}
                  >
                    Financial Summary
                  </h3>
                  <DetailRow>
                    <DetailLabel>Case Signature:</DetailLabel>
                    <DetailValue>{claim.caseSignature}</DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Total Charges:</DetailLabel>
                    <DetailValue>
                      {formatCurrency(claim.chargeAmount)}
                    </DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Claim Amount:</DetailLabel>
                    <DetailValue>
                      {formatCurrency(claim.claimAmount)}
                    </DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Expected Amount:</DetailLabel>
                    <DetailValue>
                      {formatCurrency(claim.expectedAmount)}
                    </DetailValue>
                  </DetailRow>
                  <DetailRow>
                    <DetailLabel>Difference:</DetailLabel>
                    <DetailValue
                      style={
                        claim.amountDifference && claim.amountDifference < 0
                          ? { fontWeight: "bold", color: "#dc3545" }
                          : undefined
                      }
                    >
                      {formatCurrency(claim.amountDifference)}
                    </DetailValue>
                  </DetailRow>
                </div>
              </DetailGrid>

              {/* Line Items Table */}
              <h3
                style={{
                  marginTop: "1.5rem",
                  marginBottom: "1rem",
                  fontSize: "16px",
                  fontWeight: 600,
                }}
              >
                Line Items
              </h3>
              {lineItems.length > 0 ? (
                <DataTable
                  value={lineItems}
                  stripedRows
                  responsiveLayout="scroll"
                  emptyMessage="No line items available"
                  style={{ fontSize: "0.9rem" }}
                >
                  <Column
                    field="cptCode"
                    header="Code"
                    style={{ minWidth: "6rem" }}
                  />
                  <Column
                    field="modifier"
                    header="Modifier"
                    body={(rowData) => rowData.modifier || "-"}
                    style={{ minWidth: "5rem" }}
                  />
                  <Column
                    field="revenueCode"
                    header="Line Revenue Code"
                    body={(rowData) => rowData.revenueCode || "-"}
                    style={{ minWidth: "8rem" }}
                  />
                  <Column
                    field="billedUnits"
                    header="Billed Units"
                    style={{ minWidth: "6rem" }}
                  />
                  <Column
                    field="lineCharge"
                    header="Charge Amount"
                    body={(rowData) =>
                      currencyBodyTemplate(rowData, "lineCharge")
                    }
                    style={{ minWidth: "8rem" }}
                  />
                  <Column
                    field="lineAllowed"
                    header="Allowed Amount"
                    body={(rowData) =>
                      currencyBodyTemplate(rowData, "lineAllowed")
                    }
                    style={{ minWidth: "8rem" }}
                  />
                  <Column
                    field="linePaid"
                    header="Insurance amount"
                    body={(rowData) =>
                      currencyBodyTemplate(rowData, "linePaid")
                    }
                    style={{ minWidth: "8rem" }}
                  />
                  <Column
                    header="Pt amount"
                    body={patientResponsibilityTemplate}
                    style={{ minWidth: "7rem" }}
                  />
                  {renderPrAdjustmentColumns()}
                </DataTable>
              ) : (
                <PlaceholderText>No line item data available</PlaceholderText>
              )}
            </div>
          </ScrollableTabContent>
        </TabPanel>

        {/* Tab 2: Allowable Calculations */}
        <TabPanel header="Expected Amounts">
          <ScrollableTabContent>
            {claim.payerId && claim.expectedAmount !== null ? (
              <PrecalculatedAllowablesContent
                accountId={accountId}
                payerId={claim.payerId}
                caseSignature={claim.caseSignature}
                lineItems={lineItems}
              />
            ) : (
              <PlaceholderText>
                Allowable calculations not available for this claim.
              </PlaceholderText>
            )}
          </ScrollableTabContent>
        </TabPanel>

        {/* Tab 3: AI Insights */}
        <TabPanel header="AI Insights">
          <ScrollableTabContent>
            <AIInsightsTabContent
              claimId={claim.id}
              cachedAnalysis={claim.aiAnalysisResult || null}
              promptVersion={claim.promptVersion}
              resolutionStatus={claim.resolutionStatus}
              resolutionHistory={claim.resolutionHistory}
              claimData={{
                patientName: claim.patientName,
                claimNumber: claim.claimNumber,
                serviceDate: claim.serviceDate,
                claimPayerName: claim.claimPayerName,
                chargeAmount: claim.chargeAmount,
              }}
            />
          </ScrollableTabContent>
        </TabPanel>

        {/* Tab 4: Estimate Comparison */}
        <TabPanel header="Estimate Comparison">
          <ScrollableTabContent>
            <EstimateComparisonTab claim={claim} />
          </ScrollableTabContent>
        </TabPanel>
      </TabView>
    </Dialog>
  );
}
